import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ory } from 'src/auth/ory';
import { Logger } from 'src/utils/Logger';
import { useSettingsFlow } from 'src/auth/hooks/useSettingsFlow';
import { useSettingsPageConfig } from 'src/auth/hooks/useSettingsPageConfig';
import { useUserCredentials } from 'src/auth/hooks/useUserCredentials';
import { ChangePasswordForm } from 'src/auth/settings/ChangePasswordForm';
import { SsoOnlyMessage } from 'src/auth/settings/SsoOnlyMessage';
import { SuccessMessage } from 'src/auth/components/SuccessMessage';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { showError } from 'src/notifications/notifications';
import { useSessionData } from 'src/auth/SessionDataHook';
import { usePublicTenantConfig } from 'src/auth/hooks/usePublicTenantConfig';
import { SettingsSource } from 'src/auth/settings/types';

type SettingsState = 'loading' | 'ready' | 'no_session';

export const SettingsPageRoute = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFlowId = searchParams.get('flow');
  const source = searchParams.get('source');
  const [state, setState] = useState<SettingsState>('loading');
  const [createdFlowId, setCreatedFlowId] = useState<string | null>(null);
  const creatingFlowRef = useRef(false);

  const flowId = urlFlowId || createdFlowId;

  useEffect(() => {
    const checkSessionAndCreateFlow = async () => {
      try {
        await ory.toSession();

        if (!urlFlowId && !createdFlowId && !creatingFlowRef.current) {
          creatingFlowRef.current = true;
          const { data } = await ory.createBrowserSettingsFlow();
          setCreatedFlowId(data.id);
          setSearchParams({ flow: data.id }, { replace: true });
          setState('ready');
          return;
        }

        if (flowId) {
          setState('ready');
        }
      } catch (err) {
        Logger.error('No session found for settings page', err);
        showError('Your session has expired. Please log in again.');
        setState('no_session');
      }
    };

    checkSessionAndCreateFlow();
  }, [urlFlowId, createdFlowId, setSearchParams, flowId]);

  if (state === 'loading') {
    return <AuthPageLayout isLoading />;
  }

  if (state === 'no_session') {
    window.location.replace('/login');
    return null;
  }

  if (!flowId) {
    return <AuthPageLayout isLoading />;
  }

  return <SettingsPage flowId={flowId} source={source as SettingsSource} />;
};

interface SettingsPageProps {
  flowId: string;
  source: SettingsSource;
}

const SettingsPage = ({ flowId, source }: SettingsPageProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const { isLoading, error, oidcNodes, csrfToken, formAction, updatePassword, initialSuccess } =
    useSettingsFlow(flowId);

  const { session, isLoading: isSessionLoading } = useSessionData();
  const tenantName = session?.identity?.metadata_public?.tenantId;
  const { config: tenantConfig, isLoading: isConfigLoading } = usePublicTenantConfig(tenantName);
  const { hasPasswordSet, isLoading: isCredentialsLoading } = useUserCredentials();

  const isLoadingAny = isLoading || isSessionLoading || isConfigLoading || isCredentialsLoading;

  const config = useSettingsPageConfig({
    source,
    authMethod: tenantConfig?.authMethod ?? 'password',
    oidcNodes,
    hasPasswordSet,
  });

  const displaySuccess = showSuccess || initialSuccess;
  const isRecoveryFlow = source === 'recovery';

  const handleUpdatePassword = async (password: string) => {
    setIsSubmitting(true);
    const { success } = await updatePassword(password);
    setIsSubmitting(false);

    if (success) {
      setShowSuccess(true);
    }
  };

  const handleRecoveryRedirect = async () => {
    try {
      const { data: logoutFlow } = await ory.createBrowserLogoutFlow();
      await ory.updateLogoutFlow({ token: logoutFlow.logout_token });
    } catch (error) {
      Logger.error('Failed to logout after password reset', error);
    }
    window.location.href = '/login';
  };

  const handleBackToLogin = () => {
    window.location.href = '/login';
  };

  return (
    <AuthPageLayout isLoading={isLoadingAny} withHeader>
      {displaySuccess ? (
        <SuccessMessage
          variant={isRecoveryFlow ? 'recovery' : 'settings'}
          onAction={isRecoveryFlow ? handleRecoveryRedirect : undefined}
        />
      ) : config.showSsoOnlyMessage ? (
        <SsoOnlyMessage
          variant={isRecoveryFlow ? 'recovery' : 'platform'}
          onBackToLogin={handleBackToLogin}
        />
      ) : (
        <ChangePasswordForm
          config={config}
          error={error}
          isSubmitting={isSubmitting}
          onSubmit={handleUpdatePassword}
          oidcNodes={oidcNodes}
          formAction={formAction}
          csrfToken={csrfToken}
        />
      )}
    </AuthPageLayout>
  );
};
